// src/common/components/ThemeEditor/ComponentPropertyEditor.tsx
import React from 'react';
import { Form, Input, Slider, ColorPicker, Space, Divider } from 'antd';
import type { Color } from 'antd/es/color-picker';

interface ComponentPropertyEditorProps {
  componentName: string;
  properties: Record<string, unknown>;
  onPropertyChange: (componentName: string, property: string, value: unknown) => void;
}

const ComponentPropertyEditor: React.FC<ComponentPropertyEditorProps> = ({
  componentName,
  properties = {},
  onPropertyChange,
}) => {
  const handleColorChange = (color: Color, property: string) => {
    onPropertyChange(componentName, property, color.toHexString());
  };

  const handleSliderChange = (value: number, property: string) => {
    onPropertyChange(componentName, property, value);
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>, property: string) => {
    onPropertyChange(componentName, property, e.target.value);
  };
  
  const handleNumericInputChange = (e: React.ChangeEvent<HTMLInputElement>, property: string) => {
    const value = e.target.value;
    // Allow empty string for clearing the input, otherwise parse as float
    onPropertyChange(componentName, property, value === '' ? undefined : parseFloat(value));
};


  return (
    <div style={{ marginTop: '20px' }}>
      <Divider>{`${componentName} Properties`}</Divider>

      <Form.Item label="Background Color">
        <Space>
          <ColorPicker
            value={properties.backgroundColor as string || ''}
            onChange={(color) => handleColorChange(color, 'backgroundColor')}
            showText
          />
          <Input
            value={properties.backgroundColor as string || ''}
            onChange={(e) => handleInputChange(e, 'backgroundColor')}
            style={{ width: 120 }}
          />
        </Space>
      </Form.Item>

      <Form.Item label="Text Color">
        <Space>
          <ColorPicker
            value={properties.color as string || ''}
            onChange={(color) => handleColorChange(color, 'color')}
            showText
          />
          <Input
            value={properties.color as string || ''}
            onChange={(e) => handleInputChange(e, 'color')}
            style={{ width: 120 }}
          />
        </Space>
      </Form.Item>

      <Form.Item label="Border Radius">
        <Slider
          min={0}
          max={30}
          onChange={(value) => handleSliderChange(value, 'borderRadius')}
          value={typeof properties.borderRadius === 'number' ? properties.borderRadius : 0}
        />
         <Input
            type="number"
            value={properties.borderRadius as number || 0}
            onChange={(e) => handleNumericInputChange(e, 'borderRadius')}
            style={{ width: 80 }}
        />
      </Form.Item>

      <Form.Item label="Padding (px)">
        <Input
            type="number"
            value={properties.padding as number}
            onChange={(e) => handleNumericInputChange(e, 'padding')}
            style={{ width: 80 }}
        />
      </Form.Item>

      <Form.Item label="Margin (px)">
         <Input
            type="number"
            value={properties.margin as number}
            onChange={(e) => handleNumericInputChange(e, 'margin')}
            style={{ width: 80 }}
        />
      </Form.Item>

      <Form.Item label="Box Shadow">
        <Input
          value={properties.boxShadow as string || ''}
          onChange={(e) => handleInputChange(e, 'boxShadow')}
          placeholder="e.g., 0 2px 8px rgba(0,0,0,0.15)"
        />
      </Form.Item>
    </div>
  );
};

export default ComponentPropertyEditor;